<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-10">
    <cf-country-select
      :model-value="address.countryCode"
      :countries="countries"
      name="country"
      label="Country"
      class="address-country"
      :validations="{ required: true }"
      @update:model-value="countrySelectedHandler"
    />

    <cf-text-input
      v-if="fullNameRequired"
      v-model="address.addressee"
      type="text"
      autocomplete="name"
      label="Full Name"
      name="addressFullName"
      class="address-name"
      :validations="{
        name: true,
        required: true
      }"
    />

    <cf-autocomplete-input
      v-model="address.line1"
      autocomplete="address-line1"
      label="Street Address"
      name="addressLine1"
      class="address-line-1"
      :loading="loadingLocationData"
      :validations="{ required: true }"
      :values="locations"
      @autocompleted="addressAutocompletedHandler"
      @search-term-input="addressSearchTermInputHandler"
    />

    <cf-text-input
      v-model="address.line2"
      type="text"
      autocomplete="address-line2"
      label="Address Line 2"
      placeholder="Suite or apartment #"
      name="addressLine2"
      class="address-line-2"
    />

    <cf-text-input
      v-if="showSublocality"
      v-model="address.sublocality"
      type="text"
      autocomplete="address-level3"
      :label="labels.sublocality"
      name="sublocality"
      class="address-sublocality"
      :validations="{ required: meta.require.sublocality }"
    />

    <cf-text-input
      v-if="showCity"
      v-model="address.city"
      type="text"
      autocomplete="address-level2"
      :label="labels.city"
      name="city"
      class="address-city"
      :validations="{ required: meta.require.city }"
    />

    <div
      v-if="showState || showPostalCode"
      :class="stateAndPostalLayoutClass"
    >
      <cf-select-input
        v-if="showState && stateOptions"
        :model-value="address.state"
        :values="stateOptions"
        :label="labels.state"
        name="state"
        class="address-state"
        :filterable="true"
        :validations="{ required: meta.require.state }"
        @update:model-value="stateSelectedHandler"
      />

      <cf-text-input
        v-else-if="showState"
        v-model="address.state"
        type="text"
        autocomplete="address-level1"
        :label="labels.state"
        name="state"
        class="address-state"
        :validations="{ required: meta.require.state }"
      />

      <cf-text-input
        v-if="showPostalCode"
        v-model="address.postalCode"
        type="text"
        autocomplete="postal-code"
        :label="labels.postalCode"
        name="postalCode"
        class="address-postal-code"
        inputmode="numeric"
        :validations="postalCodeValidations"
        :mask="{ type: 'postalCode', region: address.countryCode }"
      />
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import debounce from 'debounce';

  import events from '@/services/events';
  import logger from '@/services/logger';
  import { getCountryMeta } from '@/helpers/address-format';

  import CfAutocompleteInput from '@/view/components/shared/cf-autocomplete-input/index.vue';
  import CfCountrySelect from '@/view/components/shared/cf-country-select/index.vue';
  import CfSelectInput from '@/view/components/shared/cf-select-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';


  export default {
    name: 'CfAddressForm',

    components: {
      CfAutocompleteInput,
      CfCountrySelect,
      CfSelectInput,
      CfTextInput
    },

    inject: {
      $validator: {
        from: '$validator'
      },

      validatedForm: {
        from: 'validatedForm'
      }
    },

    props: {
      /*
        Allowlist of ISO 3166-1 alpha-2 country codes the country dropdown
        should expose. Omit to show every country (credit-card-billing case);
        pass `merchant.regions` for delivery / registration /
        account flows.
      */
      countries: {
        type: Array,
        default: null
      },

      fullNameRequired: {
        type: Boolean,
        default: false
      },

      modelValue: {
        type: Object,
        default() {
          return {};
        }
      }
    },

    data() {
      return {
        address: {
          addressee: null,
          city: null,
          countryCode: 'US',
          line1: null,
          line2: null,
          postalCode: null,
          state: null,
          sublocality: null,
          lat: null,
          long: null
        },
        addressSearchTerm: null,
        loadingLocationData: false,
        locations: [],
        selectedGooglePlaceId: null
      };
    },

    computed: {
      ...mapGetters('session', ['currentUser']),

      meta() {
        return getCountryMeta(this.address.countryCode);
      },

      labels() {
        return {
          city: this.meta.labels.city,
          state: this.meta.labels.state,
          postalCode: this.meta.labels.zip,
          sublocality: this.meta.labels.sublocality
        };
      },

      /*
        A field is rendered iff its token appears in the country's format
        string. Required-ness is a separate axis (the `require` string).
      */
      showCity() {
        return this.meta.fmt.includes('%C');
      },

      showState() {
        return this.meta.fmt.includes('%S');
      },

      showPostalCode() {
        return this.meta.fmt.includes('%Z');
      },

      showSublocality() {
        return this.meta.fmt.includes('%D');
      },

      stateOptions() {
        if (!this.meta.states) return null;
        return this.meta.states.map(s => ({ value: s.key, display: s.name }));
      },

      postalCodeValidations() {
        const validations = { required: this.meta.require.zip };
        if (this.meta.postalCodeRegex) {
          validations.regex = this.meta.postalCodeRegex;
        }
        return validations;
      },

      /*
        Two-column grid when both state and postal are visible; single-column
        flex when only one is.
      */
      stateAndPostalLayoutClass() {
        return this.showState && this.showPostalCode
          ? 'cf-grid cf-grid--2-grid-column-equal cf-grid--gap-10'
          : 'cf-flex cf-flex--column cf-flex--gap-10';
      }
    },

    watch: {
      address: {
        deep: true,
        handler: 'addressChangeHandler'
      },
      selectedGooglePlaceId: 'googlePlaceSelected',
      modelValue: {
        deep: true,
        handler: 'valueChangeHandler',
        immediate: true
      }
    },

    methods: {
      addressAutocompletedHandler(autocompletion) {
        this.googlePlaceSelected(autocompletion.value);
      },

      addressChangeHandler() {
        this.$emit('update:modelValue', this.address);
      },

      addressSearchTermInputHandler(addressSearchTerm) {
        this.addressSearchTerm = addressSearchTerm;

        this.searchGooglePlaces();
      },

      autocompleteAddressWithGooglePlaceData(data) {
        const addressComponents = {};

        data.address_components.forEach((component) => {
          component.types.forEach((type) => {
            if (type === 'street_number') {
              addressComponents.streetNumber = component.long_name;
            }
            else if (type === 'route') {
              addressComponents.streetName = component.long_name;
            }
            else if (type === 'sublocality_level_1' || type === 'sublocality') {
              addressComponents.sublocality = component.long_name;
            }
            else if (type === 'locality') {
              addressComponents.city = component.long_name;
            }
            else if (type === 'administrative_area_level_1') {
              addressComponents.state = component.short_name;
            }
            else if (type === 'postal_code') {
              addressComponents.postalCode = component.long_name;
            }
            else if (type === 'country') {
              addressComponents.country = component.short_name;
            }
          });
        });

        if (addressComponents.country) {
          this.address.countryCode = addressComponents.country;
        }

        this.address.line1 = addressComponents.streetNumber
          ? `${addressComponents.streetNumber} ${addressComponents.streetName}`
          : addressComponents.streetName;
        this.address.sublocality = addressComponents.sublocality || null;
        this.address.city = addressComponents.city;
        this.address.postalCode = addressComponents.postalCode;
        this.address.state = addressComponents.state;

        this.wipeFieldsHiddenInCountry();
      },

      countrySelectedHandler(newCode) {
        if (!newCode || newCode === this.address.countryCode) return;

        this.address.countryCode = newCode;
        this.wipeAddressFields();
      },

      /*
        When the user explicitly changes country, every geographic field is
        no longer relevant — clear them all so the user starts fresh in the
        new country. The recipient name (addressee) is the person, not the
        place, so it persists.
      */
      wipeAddressFields() {
        this.address.line1 = null;
        this.address.line2 = null;
        this.address.sublocality = null;
        this.address.city = null;
        this.address.state = null;
        this.address.postalCode = null;
        this.address.lat = null;
        this.address.long = null;
      },

      /*
        Used by the Google Places autocomplete path after fields have been
        populated from the chosen result. Drops only the fields whose tokens
        the new country's fmt doesn't include — preserves the just-extracted
        values for everything else.
      */
      wipeFieldsHiddenInCountry() {
        const { fmt } = getCountryMeta(this.address.countryCode);
        if (!fmt.includes('%S')) this.address.state = null;
        if (!fmt.includes('%C')) this.address.city = null;
        if (!fmt.includes('%Z')) this.address.postalCode = null;
        if (!fmt.includes('%D')) this.address.sublocality = null;
      },

      genericErrorHandler() {
        events.emit('notification', {
          type: 'error',
          message: 'Failed to autocomplete your address. Please fill address in manually.'
        });
      },

      async googlePlaceSelected(placeId) {
        try {
          this.loadingLocationData = true;

          const response = await this.$googleMapsService.getPlaceDetails(placeId);

          this.autocompleteAddressWithGooglePlaceData(response);
        }

        catch (error) {
          logger.error(error);

          this.genericErrorHandler();
        }

        finally {
          this.loadingLocationData = false;
        }
      },

      searchGooglePlaces: debounce(async function searchGooglePlaces() {
        try {
          if (this.addressSearchTerm.length > 1) {
            const predictionType = 'address';

            this.locations = await this.$googleMapsService.getAutoCompletePredictions(this.addressSearchTerm, predictionType);
          }
          else {
            this.locations = [];
          }
        }

        catch (error) {
          logger.error(error);
        }
      }, 500),

      stateSelectedHandler(newState) {
        this.address.state = newState;
      },

      valueChangeHandler(address) {
        /*
          Mutate in place (Object.assign) rather than replacing the whole
          object — under v-model, replacing creates a new ref on every cycle,
          the parent then re-emits that ref as modelValue, this watcher fires
          again, and Vue throws "Maximum recursive updates exceeded".
          Mutation preserves the ref so the reactivity round-trip stabilizes.
        */
        Object.assign(this.address, address);

        if (!this.address.addressee && this.currentUser.accountId) {
          this.address.addressee = this.currentUser.fullName;
        }
      }
    }
  };
</script>
